/**
 * WebSocket messages (docs/design/05 §3; owner decision Q48: no chat and no emotes).
 *
 * Client → server messages are parsed with these strict schemas (unknown keys rejected, numbers
 * range-checked, strings capped) before anything else happens; the seat always comes from the socket.
 * Server → client schemas let clients and tests validate what the server sends.
 */
import { REJECTION_CODES } from '@play/engine';
import { z } from 'zod';
import { CommandSchema } from './commands';
import { ViewEventSchema } from './events';
import { PlayerIdSchema, TierSchema, ViewerSchema, int, text } from './primitives';
import { GameResultSchema, PlayerViewSchema } from './view';

const seq = int(0, Number.MAX_SAFE_INTEGER);
const ms = int(0, 24 * 60 * 60_000);

/** Who sits where, as everyone in the match may see it (owner decision Q13). */
export const SeatInfoSchema = z.strictObject({
  seat: PlayerIdSchema,
  /** Supabase user id; `null` for the practice bot. */
  userId: z.uuid().nullable(),
  handle: z.string().max(64).nullable(),
  displayName: z.string().max(80).nullable(),
  avatarPrintingId: z.string().max(64).nullable(),
  pro: z.boolean(),
  bot: z.boolean(),
});

/** Public facts about a match, fixed at creation (docs/design/01 §6 versions). */
export const MatchHeaderSchema = z.strictObject({
  matchId: text(128),
  kind: z.enum(['queue', 'room', 'bot']),
  tier: TierSchema,
  mode: z.literal('duel'),
  format: z.literal('standard'),
  createdAt: z.iso.datetime(),
  seats: z.array(SeatInfoSchema).min(1).max(8),
  versions: z.strictObject({ protocol: int(1, 9999), engine: text(64), rules: text(64), catalog: text(64) }),
  /** H(seed ‖ matchId), published before the first shuffle (03 §12). */
  commitment: z.string().regex(/^[0-9a-f]{64}$/),
});

/** Relative clocks: remaining milliseconds, never timestamps (05 §3.2). */
export const ClocksSchema = z.strictObject({
  bankMs: z.record(PlayerIdSchema, ms),
  /** Whose clock is running, or `null` (setup between decisions, game over). */
  running: PlayerIdSchema.nullable(),
  /** Remaining time of the current decision window, or `null` when none runs. */
  decisionMs: ms.nullable(),
  window: z.enum(['decision', 'emptyWindow']).nullable(),
});

// ── Client → server ─────────────────────────────────────────────────────────────────────────────
export const ClientMsgSchema = z.discriminatedUnion('t', [
  /** First message on every connection; `lastSeq` asks for a resume. */
  z.strictObject({ t: z.literal('hello'), protocol: int(1, 9999), lastSeq: seq.exactOptional() }),
  /**
   * A command acting on the view with this `seq`. The server must reject a stale `decide` (decisions
   * belong to one state) with `stale`; table commands are re-validated against the current state, so the
   * server may apply them even when an update raced them (an opponent's arrow, for example).
   */
  z.strictObject({ t: z.literal('act'), seq, cmd: CommandSchema }),
  z.strictObject({ t: z.literal('ping'), n: int(0, Number.MAX_SAFE_INTEGER) }),
  /** Propose lowering the tier (02 §2.2); the other player answers. */
  z.strictObject({ t: z.literal('tierDowngrade'), propose: z.union([z.literal(1), z.literal(2)]) }),
  z.strictObject({ t: z.literal('tierDowngradeAnswer'), accept: z.boolean() }),
  /** Concede at any time, whatever the view seq (CR 650). */
  z.strictObject({ t: z.literal('concede') }),
]);

/** Engine rejection codes plus the server's own. */
export const RejectionCodeSchema = z.enum([
  ...REJECTION_CODES,
  /** The command acted on an old view (`act.seq` ≠ the current seq). */
  'stale',
  /** The connection is a spectator or not seated. */
  'notSeated',
  /** Too many messages. */
  'rateLimited',
  /** No tier downgrade proposal to answer, or one is already open. */
  'noProposal',
]);

// ── Server → client ─────────────────────────────────────────────────────────────────────────────
export const ServerMsgSchema = z.discriminatedUnion('t', [
  z.strictObject({
    t: z.literal('welcome'),
    protocol: int(1, 9999),
    seat: ViewerSchema,
    match: MatchHeaderSchema,
    seq,
    view: PlayerViewSchema,
    clocks: ClocksSchema,
    resumed: z.boolean(),
    /** Recent log events for this viewer (bounded), oldest first. */
    recent: z.array(ViewEventSchema),
  }),
  /** One applied command: its events for this viewer and the full new view. */
  z.strictObject({
    t: z.literal('update'),
    seq,
    events: z.array(ViewEventSchema),
    view: PlayerViewSchema,
    clocks: ClocksSchema,
  }),
  /** Your command did nothing. */
  z.strictObject({
    t: z.literal('rejected'),
    seq,
    code: RejectionCodeSchema,
    detail: z.string().max(300).exactOptional(),
  }),
  z.strictObject({ t: z.literal('pong'), n: int(0, Number.MAX_SAFE_INTEGER) }),
  z.strictObject({
    t: z.literal('presence'),
    seat: PlayerIdSchema,
    status: z.enum(['connected', 'disconnected', 'reconnecting']),
    graceMs: ms.exactOptional(),
  }),
  z.strictObject({
    t: z.literal('tierDowngradeProposed'),
    by: PlayerIdSchema,
    to: z.union([z.literal(1), z.literal(2)]),
  }),
  z.strictObject({ t: z.literal('tierDowngradeAnswered'), by: PlayerIdSchema, accept: z.boolean() }),
  /** The match is over: the result, the revealed seed (verify it against the commitment) and the replay. */
  z.strictObject({
    t: z.literal('ended'),
    result: GameResultSchema,
    seedReveal: z.string().regex(/^[0-9a-f]{32}$/),
    replayId: text(128),
  }),
  z.strictObject({
    t: z.literal('error'),
    code: z.enum(['protocol', 'auth', 'server', 'rateLimited', 'replaced']),
    reload: z.boolean().exactOptional(),
    message: z.string().max(300).exactOptional(),
  }),
]);
